package com.karyadina.ui.screens

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*

import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.InsertDriveFile
import androidx.compose.material.icons.automirrored.filled.OpenInNew
import androidx.compose.material.icons.automirrored.outlined.InsertDriveFile
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.ui.unit.dp
import androidx.compose.ui.layout.ContentScale
import androidx.compose.foundation.Image
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.karyadina.util.rememberImageBitmapFromUri

import androidx.compose.ui.unit.sp
import com.karyadina.model.ImageItem
import com.karyadina.model.PdfConfig
import com.karyadina.model.PdfConversionResult
import com.karyadina.model.PdfPageSize
import com.karyadina.model.Unit as StudyUnit

import com.karyadina.service.*
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import org.jetbrains.compose.resources.stringResource
import com.karyadina.ui.theme.StudyStrings


@OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class)
@Composable
fun ImageToPdfScreen(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val scope = rememberCoroutineScope()
    val fileService = remember { FileServiceImpl() }
    val pdfService = remember { ImageToPdfServiceImpl() }
    
    var selectedImages by remember { mutableStateOf<List<ImageItem>>(emptyList()) }
    var pdfConfig by remember { mutableStateOf(PdfConfig()) }
    var showPageSizeDialog by remember { mutableStateOf(false) }

    // Pre-load strings for coroutine usage
    val errorSelect = stringResource(StudyStrings.PdfErrorSelect)
    val errorPrefix = stringResource(StudyStrings.PdfErrorPrefix)
    val errorOpen = stringResource(StudyStrings.PdfErrorOpen)
    val errorShare = stringResource(StudyStrings.PdfErrorShare)

    var showArrangeDialog by remember { mutableStateOf(false) }
    var isConverting by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var pdfResult by remember { mutableStateOf<PdfConversionResult?>(null) }
    
    // Calculate current step
    val currentStep = when {
        pdfResult?.success == true -> 4
        selectedImages.isNotEmpty() -> 2
        else -> 1
    }
    
    // Auto-hide success message
    LaunchedEffect(successMessage) {
        if (successMessage != null) {
            kotlinx.coroutines.delay(3000)
            successMessage = null
        }
    }
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = { 
                    Column {
                        Text(stringResource(StudyStrings.PdfToolTitle), style = MaterialTheme.typography.titleLarge)
                        Text(
                            stringResource(StudyStrings.PdfToolSubtitle),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, stringResource(StudyStrings.Back))
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.surface,
                    titleContentColor = MaterialTheme.colorScheme.onSurface
                )
            )
        }
    ) { paddingValues ->
        com.karyadina.ui.components.ResponsiveContent {
            Column(
                modifier = modifier
                    .fillMaxSize()
                    .padding(paddingValues)
            ) {
            // Success/Error banner
            AnimatedVisibility(
                visible = successMessage != null || errorMessage != null,
                enter = slideInVertically() + expandVertically() + fadeIn(),
                exit = slideOutVertically() + shrinkVertically() + fadeOut()
            ) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 8.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = if (successMessage != null) 
                            MaterialTheme.colorScheme.primaryContainer
                        else MaterialTheme.colorScheme.errorContainer
                    )
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            if (successMessage != null) Icons.Filled.CheckCircle else Icons.Filled.Error,
                            contentDescription = null,
                            tint = if (successMessage != null)
                                MaterialTheme.colorScheme.onPrimaryContainer
                            else MaterialTheme.colorScheme.onErrorContainer
                        )
                        Spacer(Modifier.width(12.dp))
                        Text(
                            text = successMessage ?: errorMessage ?: "",
                            style = MaterialTheme.typography.bodyMedium,
                            color = if (successMessage != null)
                                MaterialTheme.colorScheme.onPrimaryContainer
                            else MaterialTheme.colorScheme.onErrorContainer
                        )
                    }
                }
            }
            
            // Progress indicator
            LinearProgressIndicator(
                progress = { currentStep / 4f },
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
            )
            
            Column(
                modifier = Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // STEP 1: Select Images
                AnimatedVisibility(
                    visible = pdfResult?.success != true,
                    enter = slideInVertically(initialOffsetY = { it / 2 }) + fadeIn()
                ) {
                    PdfStepCard(
                        stepNumber = 1,
                        title = stringResource(StudyStrings.PdfStep1Title),
                        subtitle = stringResource(StudyStrings.PdfStep1Subtitle),
                        isCompleted = selectedImages.isNotEmpty(),
                        isActive = selectedImages.isEmpty(),
                        icon = Icons.Filled.Collections
                    ) {
                        FilledTonalButton(
                            onClick = {
                                scope.launch {
                                    try {
                                        errorMessage = null
                                        successMessage = null
                                        val images = fileService.pickImages()
                                        if (images.isNotEmpty()) {
                                            selectedImages = images
                                        }
                                    } catch (e: Exception) {
                                        errorMessage = errorSelect
                                    }
                                }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !isConverting
                        ) {
                            Icon(Icons.Outlined.AddPhotoAlternate, null)
                            Spacer(Modifier.width(8.dp))
                            Text(if (selectedImages.isEmpty()) stringResource(StudyStrings.PdfBtnChooseImages) else stringResource(StudyStrings.PdfBtnChangeImages, selectedImages.size))
                        }
                        
                        // Display selected images
                        if (selectedImages.isNotEmpty()) {
                            Spacer(Modifier.height(12.dp))
                            
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                OutlinedCard(modifier = Modifier.weight(1f)) {
                                    Column(
                                        modifier = Modifier.padding(12.dp),
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        Icon(Icons.Outlined.CollectionsBookmark, null, tint = MaterialTheme.colorScheme.primary)
                                        Spacer(Modifier.height(4.dp))
                                        Text(
                                            "${selectedImages.size}",
                                            style = MaterialTheme.typography.titleLarge,
                                            fontWeight = FontWeight.Bold
                                        )
                                        Text(stringResource(StudyStrings.PdfStatImages), style = MaterialTheme.typography.bodySmall)
                                    }
                                }
                                
                                OutlinedCard(modifier = Modifier.weight(1f)) {
                                    Column(
                                        modifier = Modifier.padding(12.dp),
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        Icon(Icons.Outlined.Storage, null, tint = MaterialTheme.colorScheme.tertiary)
                                        Spacer(Modifier.height(4.dp))
                                        val totalSizeKB = (selectedImages.sumOf { it.sizeInKB } * 10).toInt() / 10.0
                                        Text(
                                            "$totalSizeKB KB",
                                            style = MaterialTheme.typography.titleLarge,
                                            fontWeight = FontWeight.Bold
                                        )
                                        Text(stringResource(StudyStrings.PdfStatTotalSize), style = MaterialTheme.typography.bodySmall)
                                    }
                                }
                            }
                            
                            Spacer(Modifier.height(16.dp))
                            
                            // Expandable "Arrange Images" section
                            OutlinedCard(
                                modifier = Modifier.fillMaxWidth(),
                                colors = CardDefaults.outlinedCardColors(
                                    containerColor = MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.3f)
                                )
                            ) {
                                // Header with button to open dialog
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable { showArrangeDialog = true }
                                        .padding(16.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Row(
                                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Icon(
                                            Icons.Filled.SwapVert,
                                            contentDescription = null,
                                            tint = MaterialTheme.colorScheme.primary
                                        )
                                        Column {
                                            Text(
                                                stringResource(StudyStrings.PdfArrangeTitle),
                                                style = MaterialTheme.typography.titleMedium,
                                                fontWeight = FontWeight.Bold
                                            )
                                            Text(
                                                stringResource(StudyStrings.PdfArrangeDesc),
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                            )
                                        }
                                    }
                                    
                                    Icon(
                                        Icons.AutoMirrored.Filled.OpenInNew,
                                        contentDescription = stringResource(StudyStrings.ActionOpen),
                                        tint = MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                        }
                    }
                }
                
                // STEP 2: PDF Settings
                AnimatedVisibility(
                    visible = selectedImages.isNotEmpty(),
                    enter = slideInVertically(initialOffsetY = { it / 2 }) + fadeIn()
                ) {
                    PdfStepCard(
                        stepNumber = 2,
                        title = stringResource(StudyStrings.PdfStep2Title),
                        subtitle = stringResource(StudyStrings.PdfStep2Subtitle),
                        isCompleted = pdfResult?.success == true,
                        isActive = selectedImages.isNotEmpty() && pdfResult == null,
                        icon = Icons.Filled.Settings
                    ) {
                        // Show "Edit Settings" button if PDF already created
                        if (pdfResult?.success == true) {
                            OutlinedButton(
                                onClick = {
                                    isConverting = false
                                    pdfResult = null // Reset to allow editing
                                },
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Icon(Icons.Outlined.Edit, null)
                                Spacer(Modifier.width(8.dp))
                                Text(stringResource(StudyStrings.PdfBtnEditSettings))
                            }
                            return@PdfStepCard
                        }
                        
                        // Page size
                        OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Icon(Icons.Outlined.AspectRatio, null, tint = MaterialTheme.colorScheme.primary)
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(stringResource(StudyStrings.PdfLabelPageSize), style = MaterialTheme.typography.labelMedium)
                                    Text(
                                        pdfConfig.pageSize,
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.Bold
                                    )
                                }
                                FilledTonalButton(
                                    onClick = { showPageSizeDialog = true },
                                    enabled = !isConverting
                                ) {
                                    Text(stringResource(StudyStrings.ActionChange))
                                }
                            }
                        }
                        
                        Spacer(Modifier.height(12.dp))
                        
                        // Quality slider
                        OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Outlined.Tune, null, tint = MaterialTheme.colorScheme.primary)
                                        Spacer(Modifier.width(8.dp))
                                        Text(stringResource(StudyStrings.PdfLabelQuality), style = MaterialTheme.typography.labelLarge)
                                    }
                                    Surface(
                                        shape = MaterialTheme.shapes.small,
                                        color = MaterialTheme.colorScheme.primaryContainer
                                    ) {
                                        Text(
                                            "${pdfConfig.quality}%",
                                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp),
                                            style = MaterialTheme.typography.labelMedium,
                                            fontWeight = FontWeight.Bold,
                                            color = MaterialTheme.colorScheme.onPrimaryContainer
                                        )
                                    }
                                }
                                Slider(
                                    value = pdfConfig.quality.toFloat(),
                                    onValueChange = { pdfConfig = pdfConfig.copy(quality = it.toInt()) },
                                    valueRange = 20f..100f,
                                    enabled = !isConverting
                                )
                            }
                        }
                        
                        Spacer(Modifier.height(12.dp))
                        
                        // Max size (optional)
                        OutlinedTextField(
                            value = pdfConfig.maxSizeKB?.toString() ?: "",
                            onValueChange = { 
                                pdfConfig = pdfConfig.copy(maxSizeKB = it.toIntOrNull())
                            },
                            label = { Text(stringResource(StudyStrings.PdfLabelMaxSize)) },
                            placeholder = { Text(stringResource(StudyStrings.PdfPlaceholderMaxSize)) },
                            leadingIcon = { Icon(Icons.Outlined.DataUsage, null) },
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !isConverting,
                            singleLine = true
                        )
                    }
                }
                
                // STEP 3: Convert Button
                AnimatedVisibility(
                    visible = selectedImages.isNotEmpty() && pdfResult == null,
                    enter = slideInVertically(initialOffsetY = { it / 2 }) + fadeIn()
                ) {
                    PdfStepCard(
                        stepNumber = 3,
                        title = stringResource(StudyStrings.PdfStep3Title),
                        subtitle = stringResource(StudyStrings.PdfStep3Subtitle),
                        isCompleted = false,
                        isActive = selectedImages.isNotEmpty(),
                        icon = Icons.Filled.PictureAsPdf
                    ) {
                        Button(
                            onClick = {
                                isConverting = true
                                errorMessage = null
                                successMessage = null
                                scope.launch {
                                    try {
                                        val timestamp = Clock.System.now().toEpochMilliseconds()
                                        val fileName = "karyadina_$timestamp.pdf"
                                        val result = pdfService.convertImagesToPdf(selectedImages, pdfConfig, fileName)
                                        pdfResult = result
                                        if (result.success) {
                                            successMessage = "PDF created successfully!"
                                            errorMessage = null
                                        } else {
                                            errorMessage = result.error ?: "Failed to create PDF"
                                            successMessage = null
                                        }
                                        isConverting = false
                                    } catch (e: Exception) {
                                        errorMessage = "$errorPrefix ${e.message}"
                                        successMessage = null
                                        isConverting = false
                                    }
                                }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !isConverting
                        ) {
                            if (isConverting) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(20.dp),
                                    strokeWidth = 2.dp,
                                    color = MaterialTheme.colorScheme.onPrimary
                                )
                                Spacer(Modifier.width(8.dp))
                                Text(stringResource(StudyStrings.PdfStatusConverting))
                            } else {
                                Icon(Icons.Filled.PictureAsPdf, null)
                                Spacer(Modifier.width(8.dp))
                                Text(stringResource(StudyStrings.PdfBtnCreate))
                            }
                        }
                    }
                    }
                }
                
                // STEP 4: Results
                pdfResult?.let { result ->
                    if (result.success) {
                        AnimatedVisibility(
                            visible = true,
                            enter = slideInVertically(initialOffsetY = { it }) + fadeIn()
                        ) {
                            PdfStepCard(
                                stepNumber = 4,
                                title = stringResource(StudyStrings.PdfStep4Title),
                                subtitle = stringResource(StudyStrings.PdfStep4Subtitle),
                                isCompleted = true,
                                isActive = true,
                                icon = Icons.Filled.CheckCircle
                            ) {
                                // Stats
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                                ) {
                                    OutlinedCard(modifier = Modifier.weight(1f)) {
                                        Column(
                                            modifier = Modifier.padding(16.dp),
                                            horizontalAlignment = Alignment.CenterHorizontally
                                        ) {
                                            Icon(Icons.Outlined.Description, null, tint = MaterialTheme.colorScheme.primary)
                                            Spacer(Modifier.height(4.dp))
                                            Text(
                                                "${result.pageCount}",
                                                style = MaterialTheme.typography.titleLarge,
                                                fontWeight = FontWeight.Bold
                                            )
                                            Text("Pages", style = MaterialTheme.typography.bodySmall)
                                        }
                                    }
                                    
                                    OutlinedCard(modifier = Modifier.weight(1f)) {
                                        Column(
                                            modifier = Modifier.padding(16.dp),
                                            horizontalAlignment = Alignment.CenterHorizontally
                                        ) {
                                            Icon(Icons.Outlined.Storage, null, tint = MaterialTheme.colorScheme.tertiary)
                                            Spacer(Modifier.height(4.dp))
                                            Text(
                                                "${(result.sizeInBytes / 1024.0 * 10).toInt() / 10.0} KB",
                                                style = MaterialTheme.typography.titleLarge,
                                                fontWeight = FontWeight.Bold
                                            )
                                            Text("File Size", style = MaterialTheme.typography.bodySmall)
                                        }
                                    }
                                }
                                
                                Spacer(Modifier.height(16.dp))
                                
                                Text(
                                    result.fileName ?: "PDF Document",
                                    style = MaterialTheme.typography.titleMedium,
                                    modifier = Modifier.fillMaxWidth(),
                                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                                )
                                
                                Spacer(Modifier.height(16.dp))
                                
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                                ) {
                                    FilledTonalButton(
                                        onClick = {
                                            scope.launch {
                                                try {
                                                    result.filePath?.let { path ->
                                                        PlatformFileHelper.openFile(path, "application/pdf")
                                                    }
                                                } catch (e: Exception) {
                                                    errorMessage = errorOpen
                                                }
                                            }
                                        },
                                    modifier = Modifier.weight(1f),
                                    enabled = result.filePath != null
                                ) {
                                    Icon(Icons.AutoMirrored.Filled.OpenInNew, null)
                                    Spacer(Modifier.width(8.dp))
                                    Text(stringResource(StudyStrings.ActionOpen))
                                }
                                    
                                    FilledTonalButton(
                                        onClick = {
                                            scope.launch {
                                                try {
                                                    result.filePath?.let { path ->
                                                        PlatformFileHelper.shareFile(path, "application/pdf")
                                                    }
                                                } catch (e: Exception) {
                                                    errorMessage = errorShare
                                                }
                                            }
                                        },
                                    modifier = Modifier.weight(1f),
                                    enabled = result.filePath != null
                                ) {
                                    Icon(Icons.Outlined.Share, null)
                                    Spacer(Modifier.width(8.dp))
                                    Text(stringResource(StudyStrings.ActionShare))
                                }
                                }
                                
                                Spacer(Modifier.height(12.dp))
                                
                                OutlinedButton(
                                    onClick = {
                                        isConverting = false
                                        selectedImages = emptyList()
                                        pdfResult = null
                                        pdfConfig = PdfConfig()
                                    },
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Icon(Icons.Outlined.Refresh, null)
                                    Spacer(Modifier.width(8.dp))
                                    Text(stringResource(StudyStrings.PdfBtnCreateAnother))
                                }
                            }
                        }
                    }
                }
            } // Close scrollable column
        }
    } // Close ResponsiveContent
    
    if (showArrangeDialog) {
        ArrangeImagesDialog(
            images = selectedImages,
            onDismiss = { showArrangeDialog = false },
            onUpdateImages = { 
                selectedImages = it
            }
        )
    }

    if (showPageSizeDialog) {
        PageSizeDialog(
            currentSize = pdfConfig.pageSize,
            onDismiss = { showPageSizeDialog = false },
            onSelect = { size ->
                pdfConfig = pdfConfig.copy(pageSize = size.name)
                showPageSizeDialog = false
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ArrangeImagesDialog(
    images: List<ImageItem>,
    onDismiss: () -> Unit,
    onUpdateImages: (List<ImageItem>) -> Unit
) {
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { 
                        Column {
                            Text(stringResource(StudyStrings.PdfArrangeDialogTitle))
                            Text(
                                stringResource(StudyStrings.PdfArrangePagesFormat, images.size), 
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    },
                    navigationIcon = {
                        IconButton(onClick = onDismiss) {
                            Icon(Icons.Filled.Close, stringResource(StudyStrings.ActionClose))
                        }
                    },
                    actions = {
                        TextButton(onClick = onDismiss) {
                            Text(stringResource(StudyStrings.ActionDone), fontWeight = FontWeight.Bold)
                        }
                    }
                )
            },
            modifier = Modifier.fillMaxSize()
        ) { padding ->
            LazyVerticalGrid(
                columns = GridCells.Adaptive(minSize = 160.dp),
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
                    .padding(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                contentPadding = PaddingValues(8.dp)
            ) {
                itemsIndexed(images) { index, image ->
                    ArrangeImageGridItem(
                        image = image,
                        index = index,
                        totalCount = images.size,
                        onMoveLeft = if (index > 0) {
                            {
                                val mutableList = images.toMutableList()
                                val temp = mutableList[index]
                                mutableList[index] = mutableList[index - 1]
                                mutableList[index - 1] = temp
                                onUpdateImages(mutableList)
                            }
                        } else null,
                        onMoveRight = if (index < images.size - 1) {
                            {
                                val mutableList = images.toMutableList()
                                val temp = mutableList[index]
                                mutableList[index] = mutableList[index + 1]
                                mutableList[index + 1] = temp
                                onUpdateImages(mutableList)
                            }
                        } else null,
                        onRemove = {
                            val mutableList = images.toMutableList()
                            mutableList.removeAt(index)
                            onUpdateImages(mutableList)
                        }
                    )
                }
            }
        }
    }
}

@Composable
fun ArrangeImageGridItem(
    image: ImageItem,
    index: Int,
    totalCount: Int,
    onMoveLeft: (() -> Unit)?,
    onMoveRight: (() -> Unit)?,
    onRemove: () -> Unit,
    modifier: Modifier = Modifier
) {
    var showDetails by remember { mutableStateOf(false) }
    
    // Calculate size-based status (reused logic for consistency)
    val sizeKB = image.sizeInKB
    val (statusColor, sizeLabel) = when {
        sizeKB < 200 -> androidx.compose.ui.graphics.Color(0xFF81C784) to stringResource(StudyStrings.PdfSizeOptimal) // Green
        sizeKB < 500 -> androidx.compose.ui.graphics.Color(0xFF64B5F6) to stringResource(StudyStrings.PdfSizeGood)    // Blue
        sizeKB < 1024 -> androidx.compose.ui.graphics.Color(0xFFFFB74D) to stringResource(StudyStrings.PdfSizeMedium) // Orange
        sizeKB < 2048 -> androidx.compose.ui.graphics.Color(0xFFBA68C8) to stringResource(StudyStrings.PdfSizeLarge)  // Purple
        else -> androidx.compose.ui.graphics.Color(0xFFEF5350) to stringResource(StudyStrings.PdfSizeHeavy)           // Red
    }

    ElevatedCard(
        modifier = modifier
            .fillMaxWidth()
            .aspectRatio(0.75f),
        colors = CardDefaults.elevatedCardColors(
            containerColor = MaterialTheme.colorScheme.surface
        ),
        elevation = CardDefaults.elevatedCardElevation(defaultElevation = 2.dp)
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            // Main Image Area with Gestures
            Column(modifier = Modifier.fillMaxSize()) {
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                        .background(MaterialTheme.colorScheme.surfaceVariant)
                        .pointerInput(Unit) {
                            detectTapGestures(
                                onPress = {
                                    showDetails = true
                                    tryAwaitRelease()
                                    showDetails = false
                                },
                                onTap = { /* Optional: Toggle specific selection state if needed */ }
                            )
                        }
                ) {
                    val bitmap = rememberImageBitmapFromUri(image.uri)
                    if (bitmap != null) {
                        Image(
                            bitmap = bitmap,
                            contentDescription = null,
                            contentScale = ContentScale.Crop,
                            modifier = Modifier.fillMaxSize()
                        )
                    } else {
                        Icon(
                            Icons.Filled.Image,
                            contentDescription = null,
                            modifier = Modifier.align(Alignment.Center),
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f)
                        )
                    }
                    
                    // Default Gradient overlay
                    if (!showDetails) {
                        Box(
                            modifier = Modifier
                                .align(Alignment.BottomCenter)
                                .fillMaxWidth()
                                .height(40.dp)
                                .background(
                                    Brush.verticalGradient(
                                        colors = listOf(
                                            androidx.compose.ui.graphics.Color.Transparent,
                                            androidx.compose.ui.graphics.Color.Black.copy(alpha = 0.3f)
                                        )
                                    )
                                )
                        )
                    }

                    // HOLD OVERLAY: Details View
                    androidx.compose.animation.AnimatedVisibility(
                        visible = showDetails,
                        enter = fadeIn(),
                        exit = fadeOut(),
                        modifier = Modifier.fillMaxSize()
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.85f))
                                .padding(12.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Icon(
                                    Icons.Outlined.Info,
                                    null,
                                    tint = androidx.compose.ui.graphics.Color.White,
                                    modifier = Modifier.size(32.dp)
                                )
                                Spacer(Modifier.height(8.dp))
                                Text(
                                    image.name,
                                    color = androidx.compose.ui.graphics.Color.White,
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontWeight = FontWeight.Bold,
                                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                                )
                                HorizontalDivider(
                                    modifier = Modifier.padding(vertical = 8.dp),
                                    color = androidx.compose.ui.graphics.Color.White.copy(alpha = 0.3f)
                                )
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Outlined.AspectRatio, null, tint = androidx.compose.ui.graphics.Color.White, modifier = Modifier.size(14.dp))
                                    Spacer(Modifier.width(4.dp))
                                    Text(
                                        "${image.width} x ${image.height}",
                                        color = androidx.compose.ui.graphics.Color.White,
                                        style = MaterialTheme.typography.bodySmall
                                    )
                                }
                                Spacer(Modifier.height(4.dp))
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Outlined.Storage, null, tint = statusColor, modifier = Modifier.size(14.dp))
                                    Spacer(Modifier.width(4.dp))
                                    Text(
                                        "${(image.sizeInKB * 10).toInt() / 10.0} KB ($sizeLabel)",
                                        color = statusColor,
                                        style = MaterialTheme.typography.bodySmall,
                                        fontWeight = FontWeight.Bold
                                    )
                                }
                            }
                        }
                    }
                }
                
                // Bottom Controls Area
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(48.dp)
                        .padding(horizontal = 4.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(
                        onClick = { onMoveLeft?.invoke() },
                        enabled = onMoveLeft != null,
                        modifier = Modifier.size(32.dp)
                    ) {
                        Icon(
                            Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Move Back",
                            modifier = Modifier.size(18.dp),
                            tint = if (onMoveLeft != null) 
                                MaterialTheme.colorScheme.onSurface 
                            else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.2f)
                        )
                    }
                    
                    Text(
                        "Page ${index + 1}",
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    
                    IconButton(
                        onClick = { onMoveRight?.invoke() },
                        enabled = onMoveRight != null,
                        modifier = Modifier.size(32.dp)
                    ) {
                        Icon(
                            Icons.AutoMirrored.Filled.ArrowForward,
                            contentDescription = "Move Forward",
                            modifier = Modifier.size(18.dp),
                            tint = if (onMoveRight != null) 
                                MaterialTheme.colorScheme.onSurface 
                            else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.2f)
                        )
                    }
                }
            }

            // Top Overlays (Only visible when not holding)
            if (!showDetails) {
                // Delete Button (Top Right)
                Surface(
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(8.dp)
                        .size(28.dp)
                        .clickable(onClick = onRemove),
                    shape = CircleShape,
                    color = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.9f),
                    shadowElevation = 2.dp
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            Icons.Filled.Close,
                            contentDescription = "Remove",
                            modifier = Modifier.size(16.dp),
                            tint = MaterialTheme.colorScheme.onErrorContainer
                        )
                    }
                }
                
                // Color-Coded Size Badge (Top Left)
                Surface(
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .padding(8.dp),
                    shape = MaterialTheme.shapes.extraSmall,
                    color = statusColor.copy(alpha = 0.9f), // Using status color
                    shadowElevation = 2.dp
                ) {
                    Text(
                        "${(image.sizeInKB * 10).toInt() / 10.0} KB",
                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                        style = MaterialTheme.typography.labelSmall,
                        fontSize = 10.sp,
                        color = androidx.compose.ui.graphics.Color.White // Ensuring contrast
                    )
                }
            }
        }
    }
}

@Composable
fun ImageListItem(
    image: ImageItem,
    onRemove: () -> Unit,
    modifier: Modifier = Modifier
) {
    Card(modifier = modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Image preview placeholder (shows icon for now)
            Surface(
                modifier = Modifier.size(60.dp),
                shape = MaterialTheme.shapes.small,
                color = MaterialTheme.colorScheme.surfaceVariant
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        Icons.Default.Image,
                        contentDescription = null,
                        modifier = Modifier.size(32.dp),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            
            // Image details
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    image.name,
                    style = MaterialTheme.typography.bodyLarge,
                    maxLines = 1
                )
                Spacer(modifier = Modifier.height(4.dp))
                Row(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // File size
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            Icons.AutoMirrored.Filled.InsertDriveFile,
                            contentDescription = null,
                            modifier = Modifier.size(14.dp),
                            tint = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(
                            "${(image.sizeInKB * 100).toInt() / 100.0} KB",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    
                    // Image dimensions
                    if (image.width > 0 && image.height > 0) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                Icons.Default.AspectRatio,
                                contentDescription = null,
                                modifier = Modifier.size(14.dp),
                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(
                                "${image.width} × ${image.height}",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
            
            // Remove button
            IconButton(onClick = onRemove) {
                Icon(Icons.Default.Delete, "Remove")
            }
        }
    }
}

@Composable
fun PageSizeDialog(
    currentSize: String,
    onDismiss: () -> Unit,
    onSelect: (PdfPageSize) -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Select Page Size") },
        text = {
            Column {
                PdfPageSize.entries.forEach { size ->
                    Card(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 4.dp),
                        onClick = { onSelect(size) }
                    ) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Text(
                                size.displayName,
                                style = MaterialTheme.typography.titleSmall,
                                fontWeight = FontWeight.Bold
                            )
                            Text(
                                "${size.widthPx} × ${size.heightPx} px",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}

@Composable
fun PdfStepCard(
    stepNumber: Int,
    title: String,
    subtitle: String,
    isCompleted: Boolean,
    isActive: Boolean,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    content: @Composable ColumnScope.() -> Unit
) {
    ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(
            containerColor = if (isActive) 
                MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f)
            else MaterialTheme.colorScheme.surface
        )
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Step number badge
                Surface(
                    modifier = Modifier.size(48.dp),
                    shape = CircleShape,
                    color = when {
                        isCompleted -> MaterialTheme.colorScheme.primary
                        isActive -> MaterialTheme.colorScheme.primaryContainer
                        else -> MaterialTheme.colorScheme.surfaceVariant
                    }
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        if (isCompleted) {
                            Icon(
                                Icons.Filled.CheckCircle,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.onPrimary
                            )
                        } else {
                            Icon(
                                icon,
                                contentDescription = null,
                                tint = if (isActive) 
                                    MaterialTheme.colorScheme.onPrimaryContainer 
                                else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
                
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        "Step $stepNumber",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        title,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        subtitle,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            
            if (isActive || isCompleted) {
                Spacer(Modifier.height(16.dp))
                content()
            }
        }
    }
}

@Composable
fun ImagePreviewCard(
    image: ImageItem,
    index: Int,
    totalCount: Int,
    onMoveUp: (() -> Unit)?,
    onMoveDown: (() -> Unit)?,
    onRemove: () -> Unit,
    modifier: Modifier = Modifier
) {
    var showInfo by remember { mutableStateOf(false) }

    // Calculate size-based gradient (subtle, no alarming colors)
    val sizeKB = image.sizeInKB
    val (gradientColor, sizeLabel, sizeDescription) = when {
        sizeKB < 200 -> Triple(
            androidx.compose.ui.graphics.Color(0xFF81C784), // Soft green
            "Optimal",
            "Small file size"
        )

        sizeKB < 500 -> Triple(
            androidx.compose.ui.graphics.Color(0xFF64B5F6), // Soft blue
            "Good",
            "Moderate size"
        )

        sizeKB < 1024 -> Triple(
            androidx.compose.ui.graphics.Color(0xFFFFB74D), // Soft orange
            "Medium",
            "Average size"
        )

        sizeKB < 2048 -> Triple(
            androidx.compose.ui.graphics.Color(0xFFBA68C8), // Soft purple
            "Large",
            "High quality"
        )

        else -> Triple(
            androidx.compose.ui.graphics.Color(0xFF90A4AE), // Neutral gray-blue
            "Very Large",
            "Maximum quality"
        )
    }

    ElevatedCard(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(
            containerColor = MaterialTheme.colorScheme.surface
        )
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth()
            ) {
                // Left side gradient indicator (4dp width)
                Box(
                    modifier = Modifier
                        .width(4.dp)
                        .height(72.dp)
                        .background(
                            Brush.verticalGradient(
                                colors = listOf(
                                    gradientColor.copy(alpha = 0.9f),
                                    gradientColor.copy(alpha = 0.4f),
                                    gradientColor.copy(alpha = 0.9f)
                                )
                            )
                        )
                )

                Row(
                    modifier = Modifier
                        .weight(1f)
                        .padding(12.dp),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                // Page number badge
                Surface(
                    modifier = Modifier.size(40.dp),
                    shape = CircleShape,
                    color = MaterialTheme.colorScheme.primaryContainer
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Text(
                            "${index + 1}",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onPrimaryContainer
                        )
                    }
                }

                // Image preview with size label
                Box {
                    Surface(
                        modifier = Modifier.size(64.dp),
                        shape = MaterialTheme.shapes.medium,
                        color = MaterialTheme.colorScheme.surfaceVariant,
                        tonalElevation = 2.dp,
                        border = androidx.compose.foundation.BorderStroke(
                            1.dp,
                            gradientColor.copy(alpha = 0.3f)
                        )
                    ) {
                        Box(
                            contentAlignment = Alignment.Center,
                            modifier = Modifier.fillMaxSize()
                        ) {
                            val bitmap = rememberImageBitmapFromUri(image.uri)
                            
                            if (bitmap != null) {
                                Image(
                                    bitmap = bitmap,
                                    contentDescription = null,
                                    contentScale = ContentScale.Crop,
                                    modifier = Modifier.fillMaxSize()
                                )
                            } else {
                                Icon(
                                    Icons.Filled.Image,
                                    contentDescription = null,
                                    modifier = Modifier.size(36.dp),
                                    tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.4f)
                                )
                            }

                            // Overlay with subtle gradient matching size indicator
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(
                                        Brush.verticalGradient(
                                            colors = listOf(
                                                androidx.compose.ui.graphics.Color.Transparent,
                                                gradientColor.copy(alpha = 0.08f)
                                            )
                                        )
                                    )
                            )
                        }
                    }

                    // Size label badge (informative, not alarming)
                    Surface(
                        modifier = Modifier
                            .align(Alignment.BottomEnd)
                            .offset(x = 4.dp, y = 4.dp),
                        shape = MaterialTheme.shapes.extraSmall,
                        color = gradientColor.copy(alpha = 0.9f),
                        shadowElevation = 2.dp
                    ) {
                        Text(
                            sizeLabel,
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            style = MaterialTheme.typography.labelSmall,
                            fontSize = 9.sp,
                            fontWeight = FontWeight.Bold,
                            color = androidx.compose.ui.graphics.Color.White
                        )
                    }
                }

                // Image details
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    Text(
                        image.name,
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.SemiBold,
                        maxLines = 1,
                        overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis
                    )

                    // Size with subtle gradient background - horizontal layout
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        // Size badge with gradient color
                        Surface(
                            shape = MaterialTheme.shapes.extraSmall,
                            color = gradientColor.copy(alpha = 0.12f),
                            border = androidx.compose.foundation.BorderStroke(
                                0.5.dp,
                                gradientColor.copy(alpha = 0.3f)
                            )
                        ) {
                            Row(
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                horizontalArrangement = Arrangement.spacedBy(4.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Box(
                                    modifier = Modifier
                                        .size(6.dp)
                                        .background(gradientColor, CircleShape)
                                )
                                Text(
                                    if (sizeKB < 1024) "${(sizeKB * 10).toInt() / 10.0} KB" else "${(sizeKB / 1024.0 * 10).toInt() / 10.0} MB",
                                    style = MaterialTheme.typography.labelSmall,
                                    fontWeight = FontWeight.Medium,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                                )
                            }
                        }

                        // Size description - compact
                        Text(
                            "• $sizeLabel",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
                            maxLines = 1,
                            overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis
                        )
                    }
                }

                // Action buttons row
                Row(
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Info button
                    IconButton(
                        onClick = { showInfo = !showInfo },
                        modifier = Modifier.size(36.dp)
                    ) {
                        Icon(
                            if (showInfo) Icons.Filled.Info else Icons.Outlined.Info,
                            "Info",
                            modifier = Modifier.size(20.dp),
                            tint = MaterialTheme.colorScheme.primary
                        )
                    }

                    // Move up button
                    IconButton(
                        onClick = { onMoveUp?.invoke() },
                        enabled = onMoveUp != null,
                        modifier = Modifier.size(36.dp)
                    ) {
                        Icon(
                            Icons.Filled.KeyboardArrowUp,
                            "Move Up",
                            modifier = Modifier.size(20.dp),
                            tint = if (onMoveUp != null)
                                MaterialTheme.colorScheme.primary
                            else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f)
                        )
                    }

                    // Move down button
                    IconButton(
                        onClick = { onMoveDown?.invoke() },
                        enabled = onMoveDown != null,
                        modifier = Modifier.size(36.dp)
                    ) {
                        Icon(
                            Icons.Filled.KeyboardArrowDown,
                            "Move Down",
                            modifier = Modifier.size(20.dp),
                            tint = if (onMoveDown != null)
                                MaterialTheme.colorScheme.primary
                            else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f)
                        )
                    }

                    // Remove button
                    IconButton(
                        onClick = onRemove,
                        modifier = Modifier.size(36.dp)
                    ) {
                        Icon(
                            Icons.Outlined.Delete,
                            "Remove",
                            modifier = Modifier.size(20.dp),
                            tint = MaterialTheme.colorScheme.error
                        )
                    }
                }
            }
        }

        // Expanded info section (outside the main Row, in the Column)
        AnimatedVisibility(
            visible = showInfo,
            enter = expandVertically() + fadeIn(),
            exit = shrinkVertically() + fadeOut()
        ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(
                            Brush.horizontalGradient(
                                colors = listOf(
                                    MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f),
                                    gradientColor.copy(alpha = 0.05f),
                                    MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
                                )
                            )
                        )
                        .padding(12.dp)
                ) {
                    HorizontalDivider(modifier = Modifier.padding(bottom = 8.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        // File size details with gradient indicator
                        Column(modifier = Modifier.weight(1f)) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(6.dp)
                            ) {
                                Icon(
                                    Icons.Outlined.Storage,
                                    null,
                                    modifier = Modifier.size(16.dp),
                                    tint = gradientColor
                                )
                                Text(
                                    "Size",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                            Text(
                                "${image.sizeInBytes} bytes",
                                style = MaterialTheme.typography.bodySmall,
                                fontWeight = FontWeight.Medium
                            )
                            // Show size category with description
                            Surface(
                                shape = MaterialTheme.shapes.extraSmall,
                                color = gradientColor.copy(alpha = 0.15f),
                                border = androidx.compose.foundation.BorderStroke(
                                    0.5.dp,
                                    gradientColor.copy(alpha = 0.3f)
                                )
                            ) {
                                Row(
                                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .size(6.dp)
                                            .background(gradientColor, CircleShape)
                                    )
                                    Text(
                                        "$sizeLabel - $sizeDescription",
                                        style = MaterialTheme.typography.labelSmall,
                                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                                    )
                                }
                            }
                        }

                        // Dimensions
                        if (image.width > 0 && image.height > 0) {
                            Column(modifier = Modifier.weight(1f)) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                                ) {
                                    Icon(
                                        Icons.Outlined.AspectRatio,
                                        null,
                                        modifier = Modifier.size(16.dp),
                                        tint = MaterialTheme.colorScheme.primary
                                    )
                                    Text(
                                        "Dimensions",
                                        style = MaterialTheme.typography.labelSmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                                Text(
                                    "${image.width} × ${image.height} px",
                                    style = MaterialTheme.typography.bodySmall,
                                    fontWeight = FontWeight.Medium
                                )
                                Text(
                                    "${(image.width * image.height / 1000000.0 * 10).toInt() / 10.0} MP",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }

                        // Type
                        Column(modifier = Modifier.weight(1f)) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(6.dp)
                            ) {
                                Icon(
                                    Icons.AutoMirrored.Outlined.InsertDriveFile,
                                    null,
                                    modifier = Modifier.size(16.dp),
                                    tint = MaterialTheme.colorScheme.primary
                                )
                                Text(
                                    "Type",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                            Text(
                                image.mimeType.substringAfter("/").uppercase(),
                                style = MaterialTheme.typography.bodySmall,
                                fontWeight = FontWeight.Medium
                            )
                            Text(
                                "Image file",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
        }
    }
}

